import { Circle } from 'lucide-react';
import type { DepartmentStatus } from '@/types/api';
import { cn } from '@/lib/utils';

interface DepartmentStatusRowProps {
  readonly department: DepartmentStatus;
}

const MODE_CONFIG = {
  monitor: { label: 'Monitor', chipClass: 'bg-teal/10 text-teal' },
  draft: { label: 'Draft', chipClass: 'bg-compliance-medium/10 text-compliance-medium' },
  proactive: { label: 'Autonomous', chipClass: 'bg-teal/20 text-teal-light' },
} as const;

function getStatusDotColor(pendingReviews: number): string {
  if (pendingReviews >= 3) return 'text-compliance-critical fill-compliance-critical';
  if (pendingReviews >= 1) return 'text-compliance-high fill-compliance-high';
  return 'text-compliance-low fill-compliance-low';
}

export function DepartmentStatusRow({ department }: DepartmentStatusRowProps) {
  const mode = MODE_CONFIG[department.agentMode];
  const dotColor = getStatusDotColor(department.pendingReviews);

  return (
    <div className="flex items-center justify-between rounded-md px-3 py-2.5 transition-colors hover:bg-muted/30">
      <div className="flex items-center gap-3 min-w-0">
        <Circle className={cn('h-2.5 w-2.5 shrink-0', dotColor)} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">
            {department.displayName}
          </p>
          <span className={cn('inline-block mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold', mode.chipClass)}>
            {mode.label}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4 shrink-0">
        {department.pendingReviews > 0 && (
          <span className="flex items-center gap-1 text-xs text-compliance-high">
            <span className="font-semibold">{department.pendingReviews}</span>
            <span className="text-muted-foreground">pending review{department.pendingReviews !== 1 ? 's' : ''}</span>
          </span>
        )}
        {department.queuedPublishes > 0 && (
          <span className="flex items-center gap-1 text-xs text-teal">
            <span className="font-semibold">{department.queuedPublishes}</span>
            <span className="text-muted-foreground">ready to publish</span>
          </span>
        )}
        {department.pendingReviews === 0 && department.queuedPublishes === 0 && (
          <span className="text-xs text-muted-foreground">Up to date</span>
        )}
      </div>
    </div>
  );
}
